import { CellAction, CellModel, CellSource, ColumnSource, RequestFilters, RowSource } from "@omegagrid/grid";
import { ColorTypes, MenuItem, actions } from "@omegagrid/core";
import { msg } from "@omegagrid/localize";
import rp, { DEFAULT_GRID_EDITOR_OPTIONS, GridEditorAdapter, GridEditorOptions, RowData } from "@reporting/rp-app-host";
import { StatementDefinition } from "@reporting/rp-types/definition";
import { EntityAccessType } from "@reporting/rp-types/users";

const PAGE_SIZE = 500;

export class FinReportsGridEditorAdapter extends GridEditorAdapter {

	private _columns: ColumnSource[];

	async getColumns(): Promise<ColumnSource[]> {
		if (this._columns) return this._columns;
		this._columns = [{
			data: 'controls',
			t: 'a',
			e: false,
			w: 5 + 20 * 4,
			content: [{
				action: 'delete',
				title: msg('Delete'),
				icon: 'trash',
				color: ColorTypes.Red
			}],
			filter: false,
			sorting: false,
		}, {
			data: 'id_statement',
			header: '#',
			t: 'n',
			z: '0',
			e: false,
			w: 80,
			s: { alignment: { horizontal: 'center' } }
		}, {
			data: 'statement',
			header: msg('Report'),
			t: 't',
			w: 200
		}, {
			data: 'remark',
			header: msg('Remark'),
			t: 't',
			w: 200
		}, {
			data: 'default_value',
			header: msg('Indicator'),
			t: 's',
			w: 115,
			content: [
				{ k: 'PS', v: msg('Opening balance') },
				{ k: 'MD', v: msg('Debit') },
				{ k: 'D', v: msg('Credit') },
				{ k: 'O', v: msg('Turnover') },
				{ k: 'KS', v: msg('Closing balance') }
			]
		}, {
			data: 'ord_num',
			header: msg('Order'),
			t: 'n',
			w: 70,
		}, {
			data: 'number_dec',
			header: msg('Decimal digit count'),
			t: 'n',
			w: 135,
		}, {
			data: 'empty_rows',
			header: msg('Show empty rows'),
			t: 'b',
			w: 180,
		}, {
			data: 'nulls_to_zeros',
			header: msg('Show zeros'),
			t: 'b',
			w: 135,
		}, {
			data: 'disabled',
			header: msg('Disable recalculation'),
			t: 'b',
			w: 150,
		}, {
			data: 'report_condition',
			header: msg('Report condition'),
			t: 't',
			w: 200,
		}, {
			data: 'who_created_dv',
			header: msg('Created by'),
			t: 't',
			e: false,
			w: 160,
		}, {
			data: 'date_created',
			header: msg('Created'),
			t: 'dt',
			e: false,
			w: 145,
		}, {
			data: 'idz',
			t: 'n',
			hidden: true,
			e: false,
		}];
		return this._columns;
	}

	async getRows(filters?: RequestFilters): Promise<RowSource[]> {
		const statements = await rp.ms('definition').get<StatementDefinition[]>('statements', this.createRequestParams(filters, {
			fields: ['idz', 'id_statement', 'statement', 'remark', 'default_value', 'ord_num', 'number_dec', 'empty_rows', 'nulls_to_zeros', 'disabled', 'report_condition', 'who_created_dv', 'date_created', 'accounting'],
			count: PAGE_SIZE
		}));
		return statements.map(data => ({ e: data.accounting !== rp.accountingProvider.a ? false : null, data: {
			controls: this.getRowControls(data.accounting !== rp.accountingProvider.a),
			...data
		}}));
	}

	getRowControls(readOnly: boolean): Partial<CellSource> {
		const drowpdownItems: MenuItem[] = [
			{ k: 'aggregatedRows', v: msg('Aggregated rows'), icon: 'arrow-down-left-and-arrow-up-right-to-center', divider: readOnly },
		];
		if (!readOnly) {
			drowpdownItems.push(
				{ k: 'copyTree', v: msg('Copy tree'), icon: 'clone' },
				{ k: 'accountsDelete', v: msg('Accounts deletion'), icon: 'trash-list', divider: true },
				{ k: 'access', v: msg('Access'), icon: 'shield-check' },
				{ k: 'reportCopy', v: msg('Copy report'), icon: { icon: 'copy', color: 'GoldenRod' } },
				{ k: 'delete', v: msg('Delete'), icon: { icon: 'trash', color: 'red' }, divider: true },
			);
		}
		drowpdownItems.push(
			{ k: 'export', v: msg('Export'), icon: 'download' }, // TODO: replace with export thread, but first resolve access rights
		);

		const items: CellAction[] = [
			{ action: 'tree', title: msg('Tree'), icon: 'list-tree', color: ColorTypes.Accent },
			{ action: 'rows', title: msg('Rows'), icon: 'list', color: ColorTypes.Green },
			{ action: 'accounts', title: msg('Accounts'), icon: 'square-dollar', color: ColorTypes.Blue },
			{ action: 'dd', icon: 'ellipsis', color: ColorTypes.Gray, type: 'menu', items: drowpdownItems }
		];

		return { content: items };
	}

	getOptions(): Partial<GridEditorOptions> {
		return this.createOptions({
			colLetters: false,
			columnSorting: true,
			filters: true,
			pageSize: PAGE_SIZE,
			editable: true,
			refreshAfterSave: true,
			rowMenu: {
				addRows: true,
				deleteRows: true,
			},
			afterRenderer: (div: HTMLDivElement, cell: CellModel) => {
				// colorize account cell if negative_flag is true
				if (cell.column?.data == 'statement' && cell.row?.data?.disabled) {
					div.style.color = "#ab0000ff";
				}
				return DEFAULT_GRID_EDITOR_OPTIONS.afterRenderer(div, cell);
			}
		});
	}

	async save(rowData: RowData[]) {
		const data = rowData.map(row => Object.assign({ _state: row.state }, row.data));
		for (const d of data) {
			const statement = d as StatementDefinition & { _state: string };
			if (!statement.statement || statement.statement === '') {
				rp.host.bucket.showAlert({ title: msg('Error'), content: msg('Report name is required!'), type: 'warning' });
				throw new Error('Report name is required!');
			}
		}

		await rp.ms('definition').post('statements', data);
	}

	async onAction(action: string, cell: CellModel, event: actions.ActionEvent) {
		// set defaults for new row
		if (action === 'addRow') {
			cell.row.data = { default_value: 'PS' };
			return;
		}

		const data = cell?.row?.data as StatementDefinition;
		if (!data?.id_statement) return;
		switch (action) {
			case 'rows':
				rp.host.open(`definition-statementRows-${data.id_statement}`, { title: `${msg('Rows')}: ${data.statement}` });
				break;
			case 'tree':
				rp.host.open(`definition-finTree-${data.id_statement}`, { title: `${msg('Tree')}: ${data.statement}` });
				break;
			case 'accounts':
				const currentYear = new Date().getFullYear();
				rp.host.open(`definition-finAccounts-${data.id_statement}-${currentYear}`, { title: `${msg('Accounts')}: ${data.statement}` });
				break;
			case 'dd.aggregatedRows':
				rp.host.open(`definition-finAggregatedRows-${data.id_statement}`, { title: `${msg('Aggregated rows')}: ${data.statement}` });
				break;
			case 'dd.export':
				rp.host.openModalSidebar({
					id: `definition-finExport-${data.id_statement}`,
					caption: `${msg('Export')} - ${data.statement}`
				});
				break;
			case 'dd.copyTree':
				rp.host.openModalSidebar({
					id: `definition-finTreeCopy-${data.id_statement}`,
					caption: `${msg('Copy tree')} - ${data.statement}`
				});
				break;
			case 'dd.accountsDelete':
				rp.host.openModalSidebar({
					id: `definition-finAccountsDelete-${data.id_statement}`,
					caption: `${msg('Accounts deletion')} - ${data.statement}`
				});
				break;
			case 'dd.access':
				rp.host.open(`users-entityUsers-${EntityAccessType.Statement}-${data.id_statement}`, { title: `${msg('Access')}: ${data.statement}` });
				break;
			case 'dd.reportCopy':
				rp.host.openModalSidebar({ id: `definition-finReportCopy-${data.id_statement}`, caption: `${msg('Copy report')} - ${data.statement}` });
				break;
			case 'dd.delete':
				this.editor.deleteRow(cell.row);
				break;
		}
	}

}
